<template>
    <v-row>
        <v-col :cols="gridColumnCount">
            <base-card>
                <v-card-title>
                    <div class="d-flex justify-space-between flex">
                        <h5 class="ma-5 mb-0 d-flex flex" style="min-width: 130px;" >Audience</h5>
                        <v-text-field
                            style="max-width: 300px;"
                            v-model="quicksearch"
                            append-icon="mdi-magnify"
                            label="Search"
                            single-line
                            hide-details
                            class="col-5"
                            @input="triggerQuicksearch"
                        ></v-text-field>
                        <v-btn
                            class="ma-2"
                            dark
                            color="danger"
                            v-if="hasPerm('create')"
                            :to="createuri"
                        >
                            <v-icon>mdi-plus</v-icon>
                            Add Audience
                        </v-btn>
                    </div>
                </v-card-title>
                <v-data-table
                    v-model="selected"
                    :headers="headers"
                    :options.sync="pagingOptions"
                    :server-items-length="totalItemCount"
                    :items="gridData"
                    item-key="code"
                    :show-select="false"
                    class="elevation-1 table-one"
                    multi-sort
                    :footer-props="{
                        'items-per-page-options': [5, 10, 20, 50]
                    }"
                >
                    <template v-slot:item.aud_create_date="{item}">
                        <div class="d-flex align-center">
                            <p class="ma-0 font-weight-medium">
                                {{ item.aud_create_date | formatDate }}
                            </p>
                        </div>
                    </template>
                    <template v-slot:item.action="{item}">
                        <div class="d-flex">
                            <v-tooltip v-if="hasPerm('update')" top>
                                <template v-slot:activator="{on, attrs}">
                                    <v-btn
                                        dark
                                        v-bind="attrs"
                                        v-on="on"
                                        icon
                                        :to="edituri + item.code"
                                    >
                                        <v-icon>mdi-pencil-box-outline</v-icon>
                                    </v-btn>
                                </template>
                                <span>Edit</span>
                            </v-tooltip>
                            <v-tooltip v-if="hasPerm('delete')" top>
                                <template v-slot:activator="{on, attrs}">
                                    <v-btn
                                        dark
                                        v-bind="attrs"
                                        v-on="on"
                                        icon
                                        @click="deleteShowConfirmDialog(item)"
                                    >
                                        <v-icon>mdi-trash-can-outline</v-icon>
                                    </v-btn>
                                </template>
                                <span>Delete</span>
                            </v-tooltip>
                        </div>
                    </template>
                </v-data-table>
            </base-card>
        </v-col>
        <base-confirmation-dialog
            v-model="deleteDialog"
            title="Delete Audience"
            message="Are you sure you want to delete this audience? This action cannot be undone."
            confirm-text="Delete"
            cancel-text="Cancel"
            confirm-color="error"
            cancel-color="primary darken-1"
            icon="mdi-delete-alert"
            icon-color="error"
            :loading="deleteLoading"
            @confirm="deleteItem"
            @cancel="cancelDelete"
        />
        <v-snackbar
            v-model="snackbar"
            :timeout="4000"
            top
            tile
            :color="snackbarColor + ' -accent-2'"
        >
            {{ snackbarMessage }}
        </v-snackbar>
    </v-row>
</template>

<script>
import {api} from "src/api/index";
import DeleteConfirmationMixin from "@/mixins/DeleteConfirmationMixin";
import BaseConfirmationDialog from "@/components/base/BaseConfirmationDialog.vue";
import { checkPerm } from "@/data/navigation";
import { formatDateLabel } from "@/core/date-utils.js";

export default {
    metaInfo: {
        title: 'Audience List'
    },
    mixins: [DeleteConfirmationMixin],
    components: {
        "base-confirmation-dialog": BaseConfirmationDialog
    },
    data() {
        return {
            entityKey: 'audience',
            edituri: '/mng/adpoints/audience/edit/',
            createuri: '/mng/adpoints/audience/create',
            totalItemCount: 0,
            snackbar: false,
            snackbarColor: 'green',
            snackbarMessage: '',
            error: '',
            quicksearch: '',
            pagingOptions: {},
            selected: [],
            baseData: [],
            headers: [
                {text: 'Condensed Name', align: 'start', value: 'condensed_name'},
                {text: 'Unique ID', align: 'start', value: 'code'},
                {text: 'Comments', align: 'start', value: 'comments'},
                {text: 'Create Date', value: 'aud_create_date'},
                {text: 'Action', value: 'action'}
            ]
        }
    },
    filters: {
        formatDate(value) {
            return formatDateLabel(value);
        }
    },
    watch: {
        pagingOptions: {
            handler () {
                this.fetchGridData();
            },
            deep: true,
        },
    },
    computed: {
        gridData() {
            return this.baseData;
        },
        gridColumnCount() {
            return 12;
        }
    },
    methods: {
        hasPerm(action=null) {
            return checkPerm('attribute', action);
        },
        async fetchGridData() {
            let qstr = "";
            const { sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10 } = this.pagingOptions;
            qstr += "?currentPage=" + page + "&limit=" + itemsPerPage;
            for(let i=0; i<sortBy.length; i++) {
                qstr += "&sortBy[" + i + "]=" + sortBy[i];
            }
            for(let i=0; i<sortDesc.length; i++) {
                qstr += "&sortDesc[" + i + "]=" + sortDesc[i];
            }
            if(this.quicksearch && this.quicksearch.length > 2){
                qstr += "&quicksearch=" + encodeURIComponent(this.quicksearch);
            }
            const rsp = await api.audience.getList(qstr);
            this.baseData = rsp.data.data;
            this.totalItemCount = rsp.data.total;
        },
        triggerQuicksearch() {
            this.fetchGridData();
        },
        async deleteItem() {
            this.error = '';
            this.deleteLoading = true;
            try {
                await api.audience.delete(this.itemToDelete.code);
                this.showSuccessMessage('Audience deleted successfully.');
                this.deleteDialog = false;
                this.fetchGridData();
            } catch (error) {
                this.showErrorMessage(error.response?.data?.message || 'Failed to delete audience.');
            } finally {
                this.deleteLoading = false;
            }
        }
    }
}
</script>

<style lang="scss" scoped>
::v-deep .table-one {
    thead.v-data-table-header- {
        tr {
            &:hover {
                background-color: #f2f3f8;
            }
            th {
                span {
                    font-size: 16px;
                    color: #304156;
                }
            }
        }
        tr {
            td {
                padding-bottom: 20px;
                padding-top: 20px;
            }
        }
    }
    tbody {
        tr {
            &:hover {
            }
        }
    }
}
</style>
